/** Correcting a company or a site after it was created — **F-017, gate 16**.
 *
 * Creation had a form; correction had nothing, so a typo in a name, a site on the wrong grid region
 * or a company created in the wrong country could only be left as it was. The server decides what
 * may change and says why (`org/provisioning.py` holds the argument, field by field); this screen's
 * job is to **offer exactly what can succeed**.
 *
 * - **Labels are always editable** — a name, a registration number, an address. Nothing computes
 *   with them.
 * - **A fact a filed figure reads is editable only while nothing depends on it.** A company's
 *   jurisdiction, year end and parent status decide which filings exist; a site's jurisdiction and
 *   owning company decide how its records are priced and which filing counts it. The detail read
 *   says whether anything depends on them (`decisions_locked_because`), and when something does the
 *   three inputs are **not rendered** — the sentence is, with the facts read-only beside it — because
 *   a control that predictably 409s tells somebody they have a permissions problem they cannot fix
 *   (design rule 6).
 * - **The lease treatment is shown and never edited here.** A filed total reads it, so correcting a
 *   stated one is ADR-0068 option B, a dated answer that is accepted and not built. The first
 *   answer has its own control on the site's row; this form says which of the two is true.
 * - **Only what changed is sent.** The server separates "leave it" (key absent) from "empty it"
 *   (explicit `null`), so a form that posted every field would turn an untouched registration
 *   number into a request to clear it.
 * - **The consequence is said after, with the number**: a rename reaches issued filings as a frozen
 *   copy of the old name, which they keep; a grid-region correction applies to records created from
 *   now on and moves no figure that exists.
 */

import { useState } from "react";

import { FieldError } from "../components/FieldError";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import { Refused } from "../api/refusal";
import { useFormChecks } from "../components/useFormChecks";
import { Empty } from "../components/Panel";
import {
  useAmendEntity,
  useAmendFacility,
  useEntityDetail,
  useFacilityDetail,
} from "../api/orgCorrections";
import type {
  EntityAmend,
  EntityDetail,
  FacilityAmend,
  FacilityDetail,
} from "../api/orgCorrections";
import own from "./Organisation.module.css";
import { customerText } from "../lib/customerWords";
import { countryName } from "../lib/format";
import { GridRegionField } from "./GridRegionField";
import {
  JURISDICTIONS,
  LEASE_LABEL,
  MONTHS,
  SITE_TYPES,
  type FacilityTypeValue,
  type JurisdictionValue,
} from "./organisationOptions";

type Report = (result: Promise<unknown>, said: string) => Promise<void>;

function cls(...names: Array<string | false | undefined>) {
  return names.filter(Boolean).join(" ");
}

const JURISDICTION_LABEL: Record<string, string> = Object.fromEntries(
  JURISDICTIONS.map((j) => [j.value, j.label]),
);

// ---------------------------------------------------------------------------------- a company

export function EntityCorrection({
  entityId,
  onSubmit,
  onDone,
}: {
  entityId: string;
  onSubmit: Report;
  onDone: () => void;
}) {
  const detail = useEntityDetail(entityId);
  if (detail.error) {
    return (
      <ReadFailed
        of="This company"
        error={detail.error}
        onRetry={() => void detail.refetch()}
      />
    );
  }
  if (!detail.data) return <Empty>Loading…</Empty>;
  return <EntityCorrectionForm detail={detail.data} onSubmit={onSubmit} onDone={onDone} />;
}

function EntityCorrectionForm({
  detail,
  onSubmit,
  onDone,
}: {
  detail: EntityDetail;
  onSubmit: Report;
  onDone: () => void;
}) {
  const amend = useAmendEntity();
  const [name, setName] = useState(detail.legal_name);
  const [registration, setRegistration] = useState(detail.registration_number ?? "");
  const [jurisdiction, setJurisdiction] = useState(detail.jurisdiction as JurisdictionValue);
  const [month, setMonth] = useState(detail.fiscal_year_end_month);
  const [day, setDay] = useState(detail.fiscal_year_end_day);
  const [isParent, setIsParent] = useState(detail.is_parent);
  const [result, setResult] = useState<{ renamed: boolean; issued: number } | null>(null);

  const locked = detail.decisions_locked_because !== null;

  // **Only what differs.** An absent key means "leave it"; `null` means "empty it" and is sent for
  // the registration number alone, which is the one field a person may legitimately blank.
  const body: EntityAmend = {};
  if (name.trim() !== detail.legal_name) body.legal_name = name.trim();
  if (registration.trim() !== (detail.registration_number ?? "")) {
    body.registration_number = registration.trim() === "" ? null : registration.trim();
  }
  if (!locked) {
    if (jurisdiction !== detail.jurisdiction) body.jurisdiction = jurisdiction;
    if (month !== detail.fiscal_year_end_month) body.fiscal_year_end_month = month;
    if (day !== detail.fiscal_year_end_day) body.fiscal_year_end_day = day;
    if (isParent !== detail.is_parent) body.is_parent = isParent;
  }
  const dirty = Object.keys(body).length > 0;
  const [checks, attach] = useFormChecks();

  return (
    <form
      ref={attach}
      className={cls(own.form)}
      data-testid="entity-correction-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        // Nothing differs: nothing to send, and the reason is printed beside the button below. A
        // *wrong* value is different - the button stays live and the sentence is beside the box (Q-06).
        if (!dirty || amend.isPending) return;
        if (!checks.run([{ field: "name", ok: name.trim() !== "", message: "Give the company its registered name." }])) {
          return;
        }
        void onSubmit(
          amend
            .mutateAsync({ entityId: detail.id, ...body })
            .then((saved) => {
              setResult({
                renamed: saved.changed.includes("legal_name"),
                issued: saved.issued_filings,
              });
            }),
          `${name.trim()} corrected.`,
        );
      }}
    >
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Registered name</span>
        <input
          className={cls(own.input)}
          value={name}
          required
          {...checks.field("name")}
          data-testid="correct-entity-name"
          onChange={(event) => {
            checks.touch("name");
            setName(event.target.value);
          }}
        />
        <FieldError {...checks.say("name")} />
      </label>
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Registration number</span>
        <input
          className={cls(own.input)}
          value={registration}
          data-testid="correct-entity-registration"
          onChange={(event) => setRegistration(event.target.value)}
        />
        <span className={cls(own.hint)}>
          Leave it empty to record that there is none. Nothing computes with it, so it can always be
          corrected.
        </span>
      </label>

      {locked ? (
        <div data-testid="entity-decisions-locked">
          <p className={cls(own.note)}>
            Incorporated in {JURISDICTION_LABEL[detail.jurisdiction] ?? detail.jurisdiction}; the
            accounting year ends {detail.fiscal_year_end_day} {MONTHS[detail.fiscal_year_end_month - 1]};{" "}
            {detail.is_parent ? "this is the group parent" : "this is not the group parent"}.
          </p>
          <p className={cls(own.note)} data-testid="entity-locked-because">
            {customerText(detail.decisions_locked_because)}
          </p>
        </div>
      ) : (
        <>
          <label className={cls(own.field)}>
            <span className={cls(own.label)}>Where it is incorporated</span>
            <select
              className={cls(own.input)}
              value={jurisdiction}
              data-testid="correct-entity-jurisdiction"
              onChange={(event) => setJurisdiction(event.target.value as JurisdictionValue)}
            >
              {JURISDICTIONS.map((j) => (
                <option key={j.value} value={j.value}>
                  {j.label}
                </option>
              ))}
            </select>
          </label>
          <label className={cls(own.field)}>
            <span className={cls(own.label)}>Accounting year ends</span>
            <span className={cls(own.pair)}>
              <input
                type="number"
                min={1}
                max={31}
                className={cls(own.input)}
                value={day}
                aria-label="Year end day"
                data-testid="correct-entity-year-end-day"
                onChange={(event) => setDay(Number(event.target.value))}
              />
              <select
                className={cls(own.input)}
                value={month}
                aria-label="Year end month"
                data-testid="correct-entity-year-end-month"
                onChange={(event) => setMonth(Number(event.target.value))}
              >
                {MONTHS.map((label, index) => (
                  <option key={label} value={index + 1}>
                    {label}
                  </option>
                ))}
              </select>
            </span>
          </label>
          <label className={cls(own.check)}>
            <input
              type="checkbox"
              checked={isParent}
              data-testid="correct-entity-parent"
              onChange={(event) => setIsParent(event.target.checked)}
            />
            <span>
              This is the group parent
              <span className={cls(own.hint)}>
                These three decide which filings exist. They can be changed now because nothing has
                been determined or filed for this company yet.
              </span>
            </span>
          </label>
        </>
      )}

      <span className={cls(own.buttons)}>
        <button
          type="submit"
          className={cls(own.submit)}
          disabled={!dirty || amend.isPending}
          aria-describedby={dirty ? undefined : "correct-entity-nothing-changed"}
          data-testid="correct-entity-submit"
        >
          {amend.isPending ? "Saving…" : "Save the correction"}
        </button>
        {dirty || result ? null : (
          <span id="correct-entity-nothing-changed" className={cls(own.hint)} data-testid="correct-entity-nothing-changed">
            Nothing has changed yet.
          </span>
        )}
        <button
          type="button"
          className={cls(own.cancel)}
          data-testid="correct-entity-cancel"
          onClick={onDone}
        >
          {result ? "Close" : "Cancel"}
        </button>
      </span>
      {result?.renamed && result.issued > 0 ? (
        <p className={cls(own.note)} data-testid="entity-rename-issued">
          {result.issued} issued filing{result.issued === 1 ? "" : "s"} for this company hold the
          previous name in their frozen copy. They are not altered; they will stop matching
          today&rsquo;s facts when re-checked.
        </p>
      ) : null}
    </form>
  );
}

// ------------------------------------------------------------------------------------- a site

export function SiteCorrection({
  siteId,
  entities,
  onSubmit,
  onDone,
}: {
  siteId: string;
  entities: ReadonlyArray<{ id: string; name: string }>;
  onSubmit: Report;
  onDone: () => void;
}) {
  const detail = useFacilityDetail(siteId);
  if (detail.error) {
    return (
      <ReadFailed
        of="This site"
        error={detail.error}
        onRetry={() => void detail.refetch()}
      />
    );
  }
  if (!detail.data) return <Empty>Loading…</Empty>;
  return (
    <SiteCorrectionForm
      detail={detail.data}
      reload={() => detail.refetch().then((read) => read.data)}
      entities={entities}
      onSubmit={onSubmit}
      onDone={onDone}
    />
  );
}

/** The words for a site field in the sentence that says what the site now reads. */
const SITE_FIELD_WORDS: Array<[keyof FacilityAmend, string]> = [
  ["name", "name"],
  ["facility_type", "kind of site"],
  ["country", "country"],
  ["grid_region", "grid region"],
  ["address_line", "street"],
  ["locality", "town or city"],
  ["admin_area", "county or state"],
  ["postcode", "postal code"],
  ["jurisdiction", "jurisdiction"],
  ["legal_entity_id", "company"],
];

/** What the site now reads for the fields this person is changing - only those, and only where it
 * differs from what they typed, so the sentence is the difference and not a dump of the record. */
function nowReadsOf(body: FacilityAmend, now: FacilityDetail): string {
  const parts: string[] = [];
  for (const [field, words] of SITE_FIELD_WORDS) {
    if (!(field in body)) continue;
    const typed = (body as Record<string, unknown>)[field] ?? null;
    const current = (now as Record<string, unknown>)[field] ?? null;
    if (current === typed) continue;
    const reads = field === "legal_entity_id" ? now.legal_entity_name : current;
    parts.push(typeof reads === "string" && reads !== "" ? `${words} \u201c${reads}\u201d` : `${words} empty`);
  }
  return parts.join(", ") || "what you typed";
}

function SiteCorrectionForm({
  detail: opened,
  reload,
  entities,
  onSubmit,
  onDone,
}: {
  detail: FacilityDetail;
  /** Read the site again. The conflict sentence and the re-apply both need the site as it is now. */
  reload: () => Promise<FacilityDetail | undefined>;
  entities: ReadonlyArray<{ id: string; name: string }>;
  onSubmit: Report;
  onDone: () => void;
}) {
  const amend = useAmendFacility();
  // **The site as this form last knew it**: as it was opened, and after each save made from here.
  // Everything below diffs against it, never against a refetched copy - a field somebody else has
  // changed and this person never touched must not be sent back over their change on a re-apply -
  // and its `version` is what every save sends (G-P9-04).
  const [detail, setDetail] = useState(opened);
  const [conflict, setConflict] = useState<Refused | null>(null);
  const [now, setNow] = useState<FacilityDetail | null>(null);
  const [reapplying, setReapplying] = useState(false);
  const [name, setName] = useState(detail.name);
  const [siteType, setSiteType] = useState(detail.facility_type as FacilityTypeValue);
  const [country, setCountry] = useState(detail.country);
  const [grid, setGrid] = useState(detail.grid_region ?? "");
  const [address, setAddress] = useState(detail.address_line ?? "");
  const [locality, setLocality] = useState(detail.locality ?? "");
  const [area, setArea] = useState(detail.admin_area ?? "");
  const [postcode, setPostcode] = useState(detail.postcode ?? "");
  const [jurisdiction, setJurisdiction] = useState(detail.jurisdiction as JurisdictionValue);
  const [entityId, setEntityId] = useState(detail.legal_entity_id);
  const [saved, setSaved] = useState(false);
  const [forFuture, setForFuture] = useState(false);

  const locked = detail.decisions_locked_because !== null;

  // Only what differs, and an emptied optional field is an explicit `null` (see the file header).
  const body: FacilityAmend = {};
  if (name.trim() !== detail.name) body.name = name.trim();
  if (siteType !== detail.facility_type) body.facility_type = siteType;
  if (country.trim().toUpperCase() !== detail.country) body.country = country.trim().toUpperCase();
  const optional: Array<[keyof FacilityAmend, string, string | null]> = [
    ["grid_region", grid, detail.grid_region],
    ["address_line", address, detail.address_line],
    ["locality", locality, detail.locality],
    ["admin_area", area, detail.admin_area],
    ["postcode", postcode, detail.postcode],
  ];
  for (const [field, typed, stored] of optional) {
    if (typed.trim() !== (stored ?? "")) {
      (body as Record<string, string | null>)[field] = typed.trim() === "" ? null : typed.trim();
    }
  }
  if (!locked) {
    if (jurisdiction !== detail.jurisdiction) body.jurisdiction = jurisdiction;
    if (entityId !== detail.legal_entity_id) body.legal_entity_id = entityId;
  }
  const dirty = Object.keys(body).length > 0;
  const [checks, attach] = useFormChecks();

  const save = (expectedVersion: number | undefined) =>
    amend.mutateAsync({ facilityId: detail.id, ...body, expected_version: expectedVersion }).then(async (result) => {
      setSaved(true);
      setForFuture(result.applies_to_new_records_only);
      setConflict(null);
      // The next save from this form is made against what this one wrote: the version the write
      // returned, and the site as it reads now for the diff. A refetch does not reject: one that
      // fails answers with the last read, and the new version is still the right thing to send.
      const read = await reload();
      setDetail((was) => ({ ...(read ?? was), version: result.version ?? read?.version }));
    });

  const isStale = (error: unknown): error is Refused => error instanceof Refused && error.isStale;

  const submit = (expectedVersion: number | undefined) => {
    setConflict(null);
    setNow(null);
    const saving = save(expectedVersion);
    // A stale refusal is said here, beside the form that still holds what was typed, with what the
    // site now reads from a fresh read. Everything else goes to the shell's reporter as before.
    saving.catch((error: unknown) => {
      if (!isStale(error)) return;
      setConflict(error);
      void reload().then((read) => setNow(read ?? null));
    });
    // The reporter prints "corrected" for a success and the refusal for a failure. A stale one is
    // neither, so it is handed a promise that never settles: the call still clears the previous
    // message, and nothing is claimed as saved or double-reported.
    void onSubmit(
      saving.catch((error: unknown) => {
        if (isStale(error)) return new Promise<never>(() => undefined);
        throw error;
      }),
      `${name.trim()} corrected.`,
    );
  };

  const reapply = async () => {
    setReapplying(true);
    try {
      const fresh = await reload();
      if (!fresh) return;
      // The version moves on; what the diff is taken against does not.
      setDetail((was) => ({ ...was, version: fresh.version }));
      submit(fresh.version);
    } finally {
      setReapplying(false);
    }
  };

  return (
    <form
      ref={attach}
      className={cls(own.form)}
      data-testid="site-correction-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        // Nothing differs: nothing to send, and the reason is printed beside the button below. A
        // *wrong* value is different - the button stays live and the sentence is beside the box (Q-06).
        if (!dirty || amend.isPending) return;
        if (
          !checks.run([
            { field: "name", ok: name.trim() !== "", message: "Give the site a name." },
            {
              field: "country",
              ok: country.trim().length === 2,
              message: "Type the two-letter country code, such as GB or US.",
            },
          ])
        ) {
          return;
        }
        submit(detail.version);
      }}
    >
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Name</span>
        <input
          className={cls(own.input)}
          value={name}
          required
          {...checks.field("name")}
          data-testid="correct-site-name"
          onChange={(event) => {
            checks.touch("name");
            setName(event.target.value);
          }}
        />
        <FieldError {...checks.say("name")} />
        <span className={cls(own.hint)}>
          The old name is kept as a note on the site, so earlier reports can still be matched to it.
          A spreadsheet or file that still names the old site will not match this one, so export it
          again before importing.
        </span>
      </label>
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Kind of site</span>
        <select
          className={cls(own.input)}
          value={siteType}
          data-testid="correct-site-type"
          onChange={(event) => setSiteType(event.target.value as FacilityTypeValue)}
        >
          {SITE_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </label>
      {/* **Four boxes, four visible labels** (G-Wadmin-08). It was one caption over four empty
          inputs of different widths, named only for a screen reader, so a person could not tell what
          the small ones took. Each is now its own label; the group is named by the caption. */}
      <div className={cls(own.field)} role="group" aria-labelledby="correct-site-address-caption">
        <span id="correct-site-address-caption" className={cls(own.label)}>
          Address
        </span>
        <label className={cls(own.addressBox)}>
          <span className={cls(own.subLabel)}>Street and number</span>
          <input
            className={cls(own.input)}
            value={address}
            data-testid="correct-site-address"
            onChange={(event) => setAddress(event.target.value)}
          />
        </label>
        <span className={cls(own.addressRow)}>
          <label className={cls(own.addressBox)}>
            <span className={cls(own.subLabel)}>Town or city</span>
            <input
              className={cls(own.input)}
              value={locality}
              data-testid="correct-site-locality"
              onChange={(event) => setLocality(event.target.value)}
            />
          </label>
          <label className={cls(own.addressBox)}>
            <span className={cls(own.subLabel)}>County or state</span>
            <input
              className={cls(own.input)}
              value={area}
              data-testid="correct-site-area"
              onChange={(event) => setArea(event.target.value)}
            />
          </label>
          <label className={cls(own.addressBox)}>
            <span className={cls(own.subLabel)}>Postal code</span>
            <input
              className={cls(own.input)}
              value={postcode}
              data-testid="correct-site-postcode"
              onChange={(event) => setPostcode(event.target.value)}
            />
          </label>
        </span>
        <span className={cls(own.hint)}>
          An address can identify a person (a home office). It is kept on the site and never written
          to the audit chain.
        </span>
      </div>
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Country</span>
        <input
          className={cls(own.input)}
          value={country}
          maxLength={2}
          {...checks.field("country")}
          data-testid="correct-site-country"
          onChange={(event) => {
            checks.touch("country");
            setCountry(event.target.value);
          }}
        />
        <FieldError {...checks.say("country")} />
        {/* The code is what is stored; a person reads the name (G-Wadmin-07: the drawer showed a bare "US"). */}
        <span className={cls(own.hint)} data-testid="correct-site-country-name">
          {countryName(country)}
        </span>
      </label>
      <GridRegionField
        jurisdiction={jurisdiction}
        value={grid}
        stored={detail.grid_region}
        onChange={setGrid}
      />

      {locked ? (
        <div data-testid="site-decisions-locked">
          <p className={cls(own.note)}>
            In {JURISDICTION_LABEL[detail.jurisdiction] ?? detail.jurisdiction}, a site of{" "}
            {detail.legal_entity_name}.
          </p>
          <p className={cls(own.note)} data-testid="site-locked-because">
            {customerText(detail.decisions_locked_because)}
          </p>
        </div>
      ) : (
        <>
          <label className={cls(own.field)}>
            <span className={cls(own.label)}>Jurisdiction</span>
            <select
              className={cls(own.input)}
              value={jurisdiction}
              data-testid="correct-site-jurisdiction"
              onChange={(event) => {
                const next = event.target.value as JurisdictionValue;
                setJurisdiction(next);
                // A region belongs to the jurisdiction's factor set, so one chosen for the old
                // jurisdiction is not carried over: it is emptied (an explicit "not set"), and
                // returning to the stored jurisdiction brings the stored region back.
                setGrid(next === detail.jurisdiction ? (detail.grid_region ?? "") : "");
              }}
            >
              {JURISDICTIONS.map((j) => (
                <option key={j.value} value={j.value}>
                  {j.label}
                </option>
              ))}
            </select>
          </label>
          <label className={cls(own.field)}>
            <span className={cls(own.label)}>Belongs to</span>
            <select
              className={cls(own.input)}
              value={entityId}
              data-testid="correct-site-company"
              onChange={(event) => setEntityId(event.target.value)}
            >
              {entities.map((entity) => (
                <option key={entity.id} value={entity.id}>
                  {entity.name}
                </option>
              ))}
            </select>
            <span className={cls(own.hint)}>
              These two choose the factors a reading is priced with and the filing that counts the
              site. They can be changed now because no reading has been recorded here yet.
            </span>
          </label>
        </>
      )}

      {/* **Shown, never offered.** A filed total reads the lease treatment, so correcting a stated
          one is a dated answer (ADR-0087 option A, built): a revision with the date it takes effect
          and a reason, reviewed against the filings it moves. That control is the "Lease history
          and corrections" button the site drawer shows below this form; saying where it is, is
          what this sentence is for (G-Wadmin-09: it used to say the correction did not exist). */}
      <p className={cls(own.note)} data-testid="site-lease-not-edited">
        Lease treatment: {LEASE_LABEL[detail.lease_type] ?? detail.lease_type}.{" "}
        {detail.lease_corrections_available
          ? detail.lease_type === "unknown"
            ? "State it with “Lease history and corrections” below; it is not asked here."
            : "Not edited in this form, because a filed total reads it. Use “Lease history and corrections” below to correct it or date a change."
          : detail.lease_type === "unknown"
            ? "State it with the control on the site's row; it is not asked here."
            : "A stated treatment cannot be changed from here: a filed total reads it, and dated corrections are not available on this server."}
      </p>

      <span className={cls(own.buttons)}>
        <button
          type="submit"
          className={cls(own.submit)}
          disabled={!dirty || amend.isPending || reapplying}
          aria-describedby={dirty ? undefined : "correct-site-nothing-changed"}
          data-testid="correct-site-submit"
        >
          {amend.isPending ? "Saving…" : "Save the correction"}
        </button>
        {dirty || saved ? null : (
          <span id="correct-site-nothing-changed" className={cls(own.hint)} data-testid="correct-site-nothing-changed">
            Nothing has changed yet.
          </span>
        )}
        <button
          type="button"
          className={cls(own.cancel)}
          data-testid="correct-site-cancel"
          onClick={onDone}
        >
          {saved ? "Close" : "Cancel"}
        </button>
      </span>
      <WriteFailed
        error={conflict}
        of="this site"
        nowReads={now ? nowReadsOf(body, now) : undefined}
        onReapply={() => void reapply()}
        reapplying={reapplying}
      />
      {forFuture ? (
        <p className={cls(own.note)} data-testid="site-grid-saved-for-future">
          The grid region applies to readings recorded from now on.
        </p>
      ) : null}
    </form>
  );
}
